import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_LABELS, STATUS_COLORS, ORIGINE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Package, Camera, Loader2, Pencil, X } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

export default function Acquisti() {
  const { text } = useSettings();
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "compra";
  const [items, setItems] = useState([]);
  const [posteggi, setPosteggi] = useState([]);
  const [pickers, setPickers] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [form, setForm] = useState({ origine: "mercato", posteggio_id: "", fornitore_id: "", prodotto: "", colli: "", unita: "colli", peso_kg: "", prezzo_unitario: "", foto_file_id: null, picker_id: "", note: "" });
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { origine: "mercato", posteggio_id: "", fornitore_id: "", prodotto: "", colli: "", unita: "colli", peso_kg: "", prezzo_unitario: "", foto_file_id: null, picker_id: "", note: "" };
  const startEdit = (a) => {
    setEditId(a.id);
    setForm({
      origine: a.origine || "mercato",
      posteggio_id: a.posteggio_id || "",
      fornitore_id: a.fornitore_id || "",
      prodotto: a.prodotto || "",
      colli: a.colli != null ? String(a.colli) : "",
      unita: a.unita || "colli",
      peso_kg: a.peso_kg != null ? String(a.peso_kg) : "",
      prezzo_unitario: a.prezzo_unitario != null ? String(a.prezzo_unitario) : "",
      foto_file_id: a.foto_file_id || null,
      picker_id: a.picker_id || "",
      note: a.note || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setForm(emptyForm); };

  const load = async () => {
    const [a, p, u, f] = await Promise.all([
      api.get("/acquisti"),
      api.get("/posteggi"),
      api.get("/users"),
      api.get("/fornitori"),
    ]);
    setItems(a.data);
    setPosteggi(p.data);
    setPickers(u.data.filter((x) => x.role === "picker"));
    setFornitori(f.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, foto_file_id: data.file_id }));
      toast.success("Foto caricata");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const create = async (e) => {
    e.preventDefault();
    const fuori = form.origine === "fuori_mercato";
    if (!form.prodotto || !form.colli || (fuori ? !form.fornitore_id : !form.posteggio_id)) {
      toast.error(fuori ? "Compila fornitore, prodotto e quantità" : "Compila posteggio, prodotto e quantità");
      return;
    }
    try {
      const payload = {
        origine: form.origine,
        posteggio_id: fuori ? null : form.posteggio_id,
        fornitore_id: fuori ? form.fornitore_id : null,
        prodotto: form.prodotto,
        colli: parseFloat(form.colli),
        unita: form.unita,
        peso_kg: form.peso_kg ? parseFloat(form.peso_kg) : null,
        prezzo_unitario: form.prezzo_unitario ? parseFloat(form.prezzo_unitario) : null,
        foto_file_id: form.foto_file_id,
        picker_id: fuori ? null : form.picker_id || null,
        note: form.note,
      };
      if (editId) {
        await api.put(`/acquisti/${editId}`, payload);
        toast.success("Acquisto aggiornato");
      } else {
        await api.post("/acquisti", payload);
        toast.success(fuori ? "Acquisto fuori mercato registrato" : "Acquisto creato e assegnato");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const assign = async (id, picker_id) => {
    try {
      await api.put(`/acquisti/${id}/assign`, { picker_id });
      toast.success("Picker assegnato");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/acquisti/${id}`);
      toast.success("Eliminato");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";
  const lbl = "text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block";
  const fuori = form.origine === "fuori_mercato";
  const totaleRiga = (parseFloat(form.colli) || 0) * (parseFloat(form.prezzo_unitario) || 0);

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("acquisti_title")}</h1>
      <p className="text-slate-400 mb-6">{text("acquisti_subtitle")}</p>

      {canEdit && (
        <form onSubmit={create} data-testid="acquisto-form" className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-8 space-y-5">
          <div className="flex items-center gap-2 pb-1 border-b border-slate-800">
            <Package className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-slate-100">{editId ? "Modifica acquisto" : "Nuovo acquisto manuale"}</h2>
          </div>

          {/* Tipo di acquisto */}
          <div>
            <label className={lbl}>Tipo di acquisto</label>
            <div className="flex gap-2" data-testid="acquisto-origine">
              <button type="button" data-testid="origine-mercato" onClick={() => setForm({ ...form, origine: "mercato" })} className={`flex-1 rounded-md px-3 py-2.5 text-sm font-semibold border transition-colors ${!fuori ? "bg-primary/15 text-primary border-primary/40" : "bg-slate-800 text-slate-400 border-slate-700 hover:text-slate-200"}`}>
                Dal mercato
              </button>
              <button type="button" data-testid="origine-fuori" onClick={() => setForm({ ...form, origine: "fuori_mercato" })} className={`flex-1 rounded-md px-3 py-2.5 text-sm font-semibold border transition-colors ${fuori ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-slate-800 text-slate-400 border-slate-700 hover:text-slate-200"}`}>
                Fuori mercato
              </button>
            </div>
          </div>

          {/* Provenienza + prodotto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>{fuori ? "Fornitore" : "Posteggio"}</label>
              {!fuori ? (
                <select data-testid="acquisto-posteggio" value={form.posteggio_id} onChange={(e) => setForm({ ...form, posteggio_id: e.target.value })} className={input}>
                  <option value="">Seleziona posteggio…</option>
                  {posteggi.map((p) => <option key={p.id} value={p.id}>{p.nome}{p.fornitore ? ` — ${p.fornitore}` : ""}</option>)}
                </select>
              ) : (
                <select data-testid="acquisto-fornitore" value={form.fornitore_id} onChange={(e) => setForm({ ...form, fornitore_id: e.target.value })} className={input}>
                  <option value="">Seleziona fornitore…</option>
                  {fornitori.map((f) => <option key={f.id} value={f.id}>{f.nome}{f.tipo === "fuori_mercato" ? " (fuori mercato)" : ""}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className={lbl}>Prodotto</label>
              <input data-testid="acquisto-prodotto" placeholder="Es. Pomodori a grappolo" value={form.prodotto} onChange={(e) => setForm({ ...form, prodotto: e.target.value })} className={input} />
            </div>
          </div>

          {/* Quantità / prezzo / peso */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className={lbl}>Quantità</label>
              <div className="flex gap-2">
                <input data-testid="acquisto-colli" type="number" step="any" placeholder="0" value={form.colli} onChange={(e) => setForm({ ...form, colli: e.target.value })} className={input} />
                <select value={form.unita} onChange={(e) => setForm({ ...form, unita: e.target.value })} className="bg-slate-800 border border-slate-700 rounded-md px-2 text-sm text-slate-100 outline-none focus:border-primary shrink-0">
                  <option value="colli">colli</option>
                  <option value="cassette">cassette</option>
                  <option value="cartoni">cartoni</option>
                  <option value="kg">kg</option>
                  <option value="pz">pz</option>
                  <option value="bancali">bancali</option>
                </select>
              </div>
            </div>
            <div>
              <label className={lbl}>Prezzo unitario (€)</label>
              <input data-testid="acquisto-prezzo" type="number" step="any" placeholder="0,00" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} className={input} />
            </div>
            <div>
              <label className={lbl}>Peso totale kg (opz.)</label>
              <input data-testid="acquisto-peso" type="number" step="any" placeholder="0" value={form.peso_kg} onChange={(e) => setForm({ ...form, peso_kg: e.target.value })} className={input} />
            </div>
          </div>

          {/* Assegnazione / foto / note */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>{fuori ? "Destinazione" : "Assegna a picker"}</label>
              {!fuori ? (
                <select data-testid="acquisto-picker" value={form.picker_id} onChange={(e) => setForm({ ...form, picker_id: e.target.value })} className={input}>
                  <option value="">Nessuno (assegna dopo)…</option>
                  {pickers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              ) : (
                <div className={`${input} flex items-center text-slate-500`}>Va diretto in accettazione</div>
              )}
            </div>
            <div>
              <label className={lbl}>Foto merce (opz.)</label>
              <label data-testid="acquisto-foto-label" className={`${input} flex items-center gap-2 cursor-pointer ${form.foto_file_id ? "text-emerald-400 border-emerald-500/40" : "text-slate-400"}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                <span className="truncate">{form.foto_file_id ? "Foto allegata ✓" : "Allega foto"}</span>
                <input data-testid="acquisto-foto-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
            </div>
            <div>
              <label className={lbl}>Note (opz.)</label>
              <input data-testid="acquisto-note" placeholder="Note aggiuntive" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
            </div>
          </div>

          {/* Totale + azioni */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between border-t border-slate-800 pt-4">
            <div className="text-right md:text-left">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Totale stimato</span>
              <div data-testid="acquisto-totale" className="font-display font-black text-2xl text-emerald-400 font-mono-plex">€ {totaleRiga.toFixed(2)}</div>
            </div>
            <div className="flex items-center gap-3">
              {editId && <button type="button" data-testid="acquisto-cancel" onClick={cancelEdit} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-4 h-4" /> Annulla</button>}
              <button data-testid="acquisto-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-5 py-2.5 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editId ? "Aggiorna acquisto" : "Registra acquisto"}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="acquisti-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Nessun acquisto registrato.</p>}
        {items.map((a) => (
          <div key={a.id} data-testid={`acquisto-${a.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 flex flex-col md:flex-row md:items-center gap-3">
            {a.foto_file_id ? (
              <a href={fileUrl(a.foto_file_id)} target="_blank" rel="noreferrer" className="shrink-0">
                <img data-testid={`foto-${a.id}`} src={fileUrl(a.foto_file_id)} alt={a.prodotto} className="w-12 h-12 rounded-md object-cover border border-slate-700" />
              </a>
            ) : (
              <div className="w-12 h-12 rounded-md bg-slate-800 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-100">{a.prodotto}</span>
                <span className="font-mono-plex text-slate-300 text-sm">{a.colli} {a.unita}</span>
                {a.prezzo_unitario != null && (
                  <span className="font-mono-plex text-emerald-400 text-sm">€ {a.prezzo_unitario.toFixed(2)}/u{a.prezzo_totale != null ? ` · tot € ${a.prezzo_totale.toFixed(2)}` : ""}</span>
                )}
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_COLORS[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                {a.origine === "fuori_mercato" && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">Fuori mercato</span>
                )}
              </div>
              <div className="text-sm text-slate-400 mt-0.5">
                {a.origine === "fuori_mercato"
                  ? (a.fornitore?.nome || "Fornitore esterno")
                  : `${a.posteggio?.nome || "—"} · Picker: `}
                {a.origine !== "fuori_mercato" && <span className="text-slate-300">{a.picker?.name || "non assegnato"}</span>}
                {a.note ? ` · ${a.note}` : ""}
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                {a.origine !== "fuori_mercato" && (
                  <select
                    data-testid={`assign-${a.id}`}
                    value={a.picker_id || ""}
                    onChange={(e) => assign(a.id, e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-primary"
                  >
                    <option value="">Assegna…</option>
                    {pickers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                <button data-testid={`edit-acquisto-${a.id}`} onClick={() => startEdit(a)} className="p-2 text-slate-500 hover:text-primary transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid={`delete-acquisto-${a.id}`} onClick={() => remove(a.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
